import { faFilePen, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Autocomplete, Box, Button, Chip, Dialog, Grid, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { api } from '../../api';
import { useAuth } from '../../units/auth/AuthProvider';
import { regLicensePlateNumber } from '../../data/LicensePlateNumber';
import moment from 'moment';
import { CarQueue } from '../../api/car-queue';

interface TimeOption {
    time: string;
    startMinutes: number;
    endMinutes: number;
}

interface DateOption {
    date: string;
}

const toDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateWithWeekday = (date: string): string => {
    const weekdayMap = ['日', '一', '二', '三', '四', '五', '六'];
    const weekday = weekdayMap[new Date(date).getDay()];
    return `${date} (週${weekday})`;
};

const intervalMinutes = 7;

const buildDateOptions = (): DateOption[] => {
    const today = new Date();
    const options: DateOption[] = [];

    for (let index = 0; index < 7; index++) {
        const date = new Date(today);
        date.setDate(today.getDate() + index);
        options.push({ date: toDateString(date) });
    }

    return options;
};

const buildTimeOptions = (): TimeOption[] => {
    const startMinutes = 9 * 60 + 30;
    const endMinutes = 20 * 60 + 30;

    const options: TimeOption[] = [];

    for (let minutes = startMinutes; minutes <= endMinutes; minutes += intervalMinutes) {
        const hours = Math.floor(minutes / 60)
            .toString()
            .padStart(2, '0');
        const mins = (minutes % 60).toString().padStart(2, '0');
        options.push({ time: `${hours}:${mins}`, startMinutes: minutes, endMinutes: minutes + intervalMinutes });
    }

    if (options[options.length - 1]?.time !== '20:30') {
        options.push({ time: '20:30', startMinutes: endMinutes, endMinutes: endMinutes + intervalMinutes });
    }

    return options;
};

const timeOptions = buildTimeOptions();
const dateOptions = buildDateOptions();

const buildEstimateTime = (date: string, time: string): Date => new Date(`${date}T${time}:00`);

export const useToReservationDialog = () => {
    const classes = useStyles();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [inputCarQueueId, setInputCarQueueId] = useState<string | undefined>(undefined);
    const [myCarIds, setMyCarIds] = useState<string[]>([]);
    const [inputCarIds, setInputCarIds] = useState<string[]>([]);
    const [inputMaxCarNumber, setInputMaxCarNumber] = useState<number>(1);
    const [tempCarId, setTempCarId] = useState('');
    const [inputEstimateTime, setInputEstimateTime] = useState<Date | undefined>(undefined);
    const [date, setDate] = useState<string | undefined>(toDateString(new Date()));
    const [time, setTime] = useState<string | undefined>(undefined);
    const [currentCarQueue, setCurrentCarQueue] = useState<CarQueue[]>([]);
    const [availableTimeOptions, setAvailableTimeOptions] = useState<TimeOption[]>(timeOptions);
    const [onConfirm, setOnConfirm] = useState<(inputCarIds: string[], inputEstimateTime: Date | undefined) => void>(() => () => {});
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onReset = () => {
        setInputCarQueueId(undefined);
        setMyCarIds([]);
        setInputCarIds([]);
        setInputMaxCarNumber(1);
        setTempCarId('');
        setInputEstimateTime(undefined);
        setDate(toDateString(new Date()));
        setTime(undefined);
        setCurrentCarQueue([]);
        setAvailableTimeOptions(timeOptions);
    };
    useEffect(() => {
        if (open) {
            auth.isAuth &&
                api.user.get({ userId: auth.profile.userId, relations: { cars: true } }).then((ret) => {
                    if (ret.data && ret.data.length > 0) setMyCarIds((ret.data[0].cars || []).map((car) => car.carId!));
                });
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        if (!date) {
            setCurrentCarQueue([]);
            setAvailableTimeOptions(timeOptions);
            return;
        }

        api.carQueue
            .get({
                complete: 'N',
                cancel: 'N',
                estimateTimeSt: moment(date).toDate(),
                estimateTimeEd: moment(date).add(1, 'day').toDate(),
            })
            .then((ret) => {
                const bookedCarQueues = ret.data || [];
                setCurrentCarQueue(bookedCarQueues);

                console.log(
                    bookedCarQueues
                        .sort((a, b) => {
                            if (!a.estimateTime) return 1;
                            if (!b.estimateTime) return -1;
                            return moment(a.estimateTime).diff(moment(b.estimateTime));
                        })
                        .map((v) => ({
                            ...v,
                            estimateTime: v.estimateTime ? moment(v.estimateTime).format('YYYY-MM-DD HH:mm') : undefined,
                        })),
                );

                const bookedTimeSet = new Set(
                    bookedCarQueues
                        .map((carQueue) => carQueue.estimateTime)
                        .filter((estimateTime): estimateTime is Date => !!estimateTime)
                        .map((estimateTime) => {
                            const estimateMoment = moment(estimateTime);
                            return estimateMoment.hours() * 60 + estimateMoment.minutes();
                        }),
                );
                const bookedTimeMinutes = Array.from(bookedTimeSet);
                const isToday = moment(date).isSame(moment(), 'day');
                const currentMinutes = moment().hours() * 60 + moment().minutes();

                const filteredTimeOptions = timeOptions.filter((option) => {
                    const hasBookedTimeInRange = bookedTimeMinutes.some(
                        (bookedMinutes) =>
                            bookedMinutes > option.startMinutes - intervalMinutes && bookedMinutes < option.endMinutes,
                    );
                    const isPastTime = isToday && option.startMinutes < currentMinutes;
                    return !hasBookedTimeInRange && !isPastTime;
                });

                setAvailableTimeOptions(filteredTimeOptions);

                if (time) {
                    const hasSelectedTime = filteredTimeOptions.some((option) => option.time === time);
                    if (!hasSelectedTime) {
                        setTime(undefined);
                        setInputEstimateTime(undefined);
                    }
                }
            });
    }, [date]);

    return {
        classes,
        auth,
        alert,
        open,
        setOpen,
        inputCarQueueId,
        setInputCarQueueId,
        myCarIds,
        setMyCarIds,
        inputCarIds,
        setInputCarIds,
        inputMaxCarNumber,
        setInputMaxCarNumber,
        tempCarId,
        setTempCarId,
        inputEstimateTime,
        setInputEstimateTime,
        date,
        setDate,
        onConfirm,
        setOnConfirm,
        onOpen,
        onClose,
        onReset,
        time,
        setTime,
        currentCarQueue,
        setCurrentCarQueue,
        availableTimeOptions,
        setAvailableTimeOptions,
    };
};

export interface ToReservationDialogProps extends ReturnType<typeof useToReservationDialog> {}

export const ToReservationDialog = (props: ToReservationDialogProps): React.ReactElement => {
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={props.open} onClose={props.onClose}>
            <AppBar position='sticky' className={props.classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {'新增車輛'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={props.onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                multiple
                                value={props.inputCarIds}
                                onChange={(event, newValue) => {
                                    const newCarIds = newValue
                                        .map((v) => v.toUpperCase())
                                        .filter((v) => v != undefined)
                                        .filter((v) => regLicensePlateNumber.test(v!))
                                        .filter((v, i) => i < props.inputMaxCarNumber);
                                    props.setInputCarIds(newCarIds);
                                }}
                                onInputChange={(event, newInputValue) => props.setTempCarId(newInputValue.toUpperCase())}
                                options={props.myCarIds}
                                getOptionLabel={(option) => option || ''}
                                filterSelectedOptions
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            <>
                                                <span style={{ color: 'red' }}>*</span>
                                                {props.tempCarId.length > 0 && props.inputCarIds.length == props.inputMaxCarNumber
                                                    ? '已超出最大數量'
                                                    : props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId)
                                                      ? `請輸入正確格式的車牌號碼，最多${props.inputMaxCarNumber}台`
                                                      : `請輸入車牌號碼，最多${props.inputMaxCarNumber}台`}
                                            </>
                                        }
                                        error={props.tempCarId.length > 0 && !regLicensePlateNumber.test(props.tempCarId)}
                                        placeholder={
                                            props.inputCarIds.length >= props.inputMaxCarNumber ? '已到達最大數量' : '請輸入車牌號碼'
                                        }
                                    />
                                )}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => <Chip label={option || ''} {...getTagProps({ index })} />)
                                }
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                options={props.availableTimeOptions}
                                groupBy={(option) => option.time.split(':')[0] + ':00'}
                                value={props.availableTimeOptions.find((option) => option.time === props.time) || null}
                                getOptionLabel={(option) => option.time}
                                onChange={(event, newValue) => {
                                    if (!newValue) {
                                        props.setTime(undefined);
                                        props.setInputEstimateTime(undefined);
                                        return;
                                    }

                                    props.setTime(newValue.time);
                                    if (props.date) {
                                        props.setInputEstimateTime(buildEstimateTime(props.date, newValue.time));
                                    }
                                }}
                                renderInput={(params) => <TextField {...params} label='選擇預約時間' />}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <Button
                                disabled={![props.inputCarIds.length > 0].every((v) => v)}
                                className={props.classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => {
                                    props.onConfirm && props.onConfirm(props.inputCarIds, props.inputEstimateTime);
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'確定新增'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
